import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ModalConfirmation from "../components/ModalConfirmation";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase-config";
import { UserContext } from "../contexts/userContext";
import { getFormattedDate } from "../utils/utils";
import { ICategory } from "../interfaces";
import { BUSINESS_COLLECTION, CATEGORIES_COLLECTION } from "../utils/constants";

const Categories = () => {
  const { businessData } = useContext(UserContext);
  const [category, setCategory] = useState<string>("");
  const [categories, setCategories] = useState([] as ICategory[]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState({} as ICategory);
  const [openModal, setOpenModal] = useState(
    {} as { show: boolean; type: "delete" | "disable" | "" }
  );

  // const getCategories = async () => {
  //   setLoading(true);

  //   const { docs } = await getDocs(
  //     query(subCollectionRef, orderBy("createdAt"))
  //   ).finally(() => setLoading(false));

  //   if (docs.length) {
  //     const list = docs.map((doc) => ({
  //       ...doc.data(),
  //       id: doc.id,
  //       createdAt: getFormattedDate(doc.data().createdAt),
  //       updatedAt: getFormattedDate(doc.data().updatedAt),
  //     })) as ICategory[];
  //     setCategories(list);
  //     localStorage.setItem("categories", JSON.stringify(list));
  //   } else setCategories([]);
  // };

  const handleClose = () => {
    setOpenModal({ show: false, type: "" });
    setSelectedCategory({} as ICategory);
  };

  const updateItem = async (valueToUpdate: any) => {
    const subCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      CATEGORIES_COLLECTION
    );
    const productRef = doc(subCollectionRef, selectedCategory.id);
    await updateDoc(productRef, valueToUpdate as any).then(() => {
      // getCategories();
      setCategory("");
      setIsEditing(false);
    });
  };

  const handleEdit = async () => {
    setLoading(true);
    const itemToUpdate = {
      name: category,
      updatedAt: new Date(),
    };

    updateItem(itemToUpdate);
  };

  const handleDelete = async () => {
    setLoading(true);
    const categoryRef = doc(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      CATEGORIES_COLLECTION,
      selectedCategory.id
    );
    await deleteDoc(categoryRef).finally(() => {
      handleClose();
      setLoading(false);
    });
  };

  const handleSwitchCategory = () => {
    const itemToUpdate = {
      status: selectedCategory.status === "enabled" ? "disabled" : "enabled",
    };
    updateItem(itemToUpdate);
    handleClose();
  };

  const handleDeleteOrSwitch = () => {
    switch (openModal.type) {
      case "delete":
        return handleDelete();
      case "disable":
        return handleSwitchCategory();
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    if (isEditing) return handleEdit();
    const subCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      CATEGORIES_COLLECTION
    );
    setLoading(true);
    const item = {
      name: category,
      status: "enabled",
      products: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await addDoc(subCollectionRef, item).then(() => {
      setCategory("");
    });
  };

  const handleShowConfirmation = (
    category: ICategory,
    type: "delete" | "disable" | ""
  ) => {
    setSelectedCategory(category);
    setOpenModal({ show: true, type });
  };

  useEffect(() => {
    setLoading(true);
    if (businessData.uid) {
      const subCollectionRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        CATEGORIES_COLLECTION
      );
      const q = query(subCollectionRef, orderBy("createdAt", "desc"));
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          if (snapshot.docs.length) {
            const list = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
              createdAt: getFormattedDate(doc.data().createdAt),
              updatedAt: getFormattedDate(doc.data().updatedAt),
            })) as ICategory[];
            setCategories(list);
            localStorage.setItem("categories", JSON.stringify(list));
          } else setCategories([]);
          setLoading(false);
        },
        (error) => {
          console.error("Error al obtener categorias en tiempo real: ", error);
          setLoading(false); // También desactivar el loading si hay un error
        }
      );

      return () => unsubscribe();
    }
  }, [businessData]);

  return (
    <Grid container spacing={3} p={3} mt={10} sx={{ width: "100%" }}>
      <Grid size={{ xs: 12 }}>
        <Typography fontSize={24}>Categorias</Typography>
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <TextField
          label="Nombre de la categoria"
          size="small"
          color="secondary"
          fullWidth
          disabled={loading}
          value={category}
          onChange={({ target: { value } }) => setCategory(value)}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          disabled={!category || loading}
          onClick={handleSubmit}
        >
          {isEditing ? "Editar " : "Agregar "}categoria
        </Button>
      </Grid>
      <Grid size={{ xs: 12 }} display="flex" justifyContent="center">
        {loading ? (
          <CircularProgress size={120} sx={{ mt: 4 }} />
        ) : (
          <>
            {categories.length > 0 ? (
              <TableContainer component={Paper}>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha de creación</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell align="center">Productos</TableCell>
                      <TableCell align="center">Ultima actualización</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categories.map((category: ICategory) => (
                      <TableRow key={category.id}>
                        <TableCell>{category.createdAt}</TableCell>
                        <TableCell>{category.name}</TableCell>
                        <TableCell align="center">
                          {category.products}
                        </TableCell>
                        <TableCell align="center">
                          {category.updatedAt}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            color={
                              category.status === "enabled"
                                ? "success"
                                : "warning"
                            }
                            label={
                              category.status === "enabled"
                                ? "Habilitada"
                                : "Deshabilitada"
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton
                              onClick={() => {
                                setCategory(category.name);
                                setSelectedCategory(category);
                                setIsEditing(true);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                            >
                              <span className="material-symbols-rounded">
                                edit
                              </span>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              color="error"
                              onClick={() =>
                                handleShowConfirmation(category, "delete")
                              }
                            >
                              <span className="material-symbols-rounded">
                                delete
                              </span>
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              category.status === "enabled"
                                ? "Deshabilitar"
                                : "Habilitar"
                            }
                          >
                            <Switch
                              size="small"
                              color="secondary"
                              checked={category.status === "enabled"}
                              onChange={() =>
                                handleShowConfirmation(category, "disable")
                              }
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="warning">No hay categorias cargadas.</Alert>
            )}
          </>
        )}
      </Grid>

      {openModal.show && (
        <ModalConfirmation
          page="category"
          status={selectedCategory.status}
          openModal={openModal}
          handleClose={handleClose}
          handleSubmit={handleDeleteOrSwitch}
        />
      )}
    </Grid>
  );
};

export default Categories;
